import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download } from "lucide-react";
import { api } from "@/lib/api";
import { downloadFile } from "@/lib/training";
import { formatDate } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/Badges";
import { TripleSlash } from "@/components/brand/Logo";

export default function Certificates() {
  const [certs, setCerts] = useState(null);
  useEffect(() => {
    api.get("/certificates").then((r) => setCerts(r.data));
  }, []);

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Certificates" title="My certificates" description="Earned by passing a course's final quiz. Each certificate has a unique ID that can be verified." />
      {certs?.length === 0 && <EmptyState title="No certificates yet" description="Complete a course and pass its quiz to earn one." testId="certificates-empty" />}
      <div className="grid gap-5 md:grid-cols-2">
        {certs?.map((c) => (
          <div key={c.id} className="relative overflow-hidden rounded-3xl border border-lasso-line bg-white p-7" data-testid={`certificate-card-${c.code}`}>
            <div className="flex items-start justify-between">
              <Award className="h-7 w-7 text-lasso-deep" />
              <TripleSlash />
            </div>
            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Certificate of completion</p>
            <h3 className="mt-2 font-display text-lg font-bold leading-snug">{c.course_title}</h3>
            <p className="mt-3 text-sm text-zinc-600">Issued {formatDate(c.issued_at)}{c.score != null ? ` · Score ${c.score}%` : ""}</p>
            {c.expires_at && <p className={`mt-1 text-sm font-semibold ${c.status === "expired" ? "text-rose-600" : c.status === "expiring" ? "text-amber-700" : "text-zinc-500"}`} data-testid={`certificate-status-${c.code}`}>
              {c.status === "expired" ? `Expired ${formatDate(c.expires_at)} — retraining due` : c.status === "superseded" ? `Expired ${formatDate(c.expires_at)} — renewed` : c.status === "expiring" ? `Expires soon: ${formatDate(c.expires_at)}` : `Valid until ${formatDate(c.expires_at)}`}
            </p>}
            <p className="mt-1 font-mono text-xs text-zinc-500" data-testid={`certificate-code-${c.code}`}>{c.code}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={() => downloadFile(`/certificates/${c.code}/pdf`, `Lasso-Hub-${c.code}.pdf`)} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid={`certificate-download-${c.code}`}>
                <Download className="h-4 w-4" /> Download PDF
              </button>
              <Link to={`/verify/${c.code}`} className="inline-flex items-center rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid={`certificate-verify-${c.code}`}>Verify</Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
